import React, { useState, useEffect, useCallback } from "react";
import { Chip, IconButton } from "@telegram-apps/telegram-ui";
import { useI18n } from "../../utils/i18n/I18nProvider";
import { getUserLanguage } from "../../utils/i18n";
import { getAvailableLanguages } from "../../configs/languages";
import { usePerformance, type PerformanceLevel } from "../../utils/performance";
import "./Settings.css";
import RefreshIcon from "./RefreshIcon";
import { SettingsStorageService } from "../../services/LocalStorageService";
import Game from "../../scripts/game/Game";

// The Settings component
export function Settings() {
  const { t, changeLanguage } = useI18n();
  const [currentLanguage, setCurrentLanguage] = useState(getUserLanguage());
  const [isBenchmarking, setIsBenchmarking] = useState(false);
  const [musicEnabled, setMusicEnabled] = useState<boolean>(
    SettingsStorageService.isMusicEnabled()
  );
  const [soundsEnabled, setSoundsEnabled] = useState<boolean>(
    SettingsStorageService.isSoundsEnabled()
  );
  const [hapticsEnabled, setHapticsEnabled] = useState<boolean>(
    SettingsStorageService.isHapticsEnabled()
  );

  // Use the performance hook
  const {
    performanceLevel,
    isAutoDetected,
    updatePerformanceLevel,
    redetectPerformance,
  } = usePerformance();

  // Get available languages from centralized config
  const availableLanguages = getAvailableLanguages();

  // Handle language change
  const handleLanguageChange = async (lang: string) => {
    setCurrentLanguage(lang);
    await changeLanguage(lang);
    localStorage.setItem("user-language", lang);
  };

  // Handle performance level change
  const handlePerformanceChange = (level: PerformanceLevel) => {
    updatePerformanceLevel(level);
  };

  // Audio/haptics handlers
  const applyMusicSetting = (enabled: boolean) => {
    SettingsStorageService.setMusicEnabled(enabled);
    try {
      if (Game.Core?.PlayerOptions) {
        Game.Core.PlayerOptions.MusicEnabled = enabled;
      }
      // Stop background music immediately if disabling
      if (!enabled) {
        const snd = Game.Core?.scene?.sound?.get("desert_music");
        snd?.stop?.();
      } else {
        // Attempt to play music if enabling and not already playing
        const existing = Game.Core?.scene?.sound?.get?.("desert_music");
        if (Game.Core?.scene && (!existing || !existing.isPlaying)) {
          const s = Game.Core.scene.sound.add("desert_music", {
            volume: 0.4,
            loop: true,
          });
          s.play();
        }
      }
    } catch {}
  };

  const applySoundsSetting = (enabled: boolean) => {
    SettingsStorageService.setSoundsEnabled(enabled);
    try {
      if (Game.Core?.PlayerOptions) {
        Game.Core.PlayerOptions.SoundsEnabled = enabled;
      }
    } catch {}
  };

  const applyHapticsSetting = (enabled: boolean) => {
    SettingsStorageService.setHapticsEnabled(enabled);
  };

  // Re-run benchmark
  const handleReBenchmark = useCallback(async () => {
    setIsBenchmarking(true);

    // Add a small delay to ensure state update is processed
    await new Promise((resolve) => setTimeout(resolve, 50));

    try {
      await redetectPerformance();
      // Performance hook will update the state
    } catch (error) {
      console.error("Performance re-detection failed:", error);
    } finally {
      setIsBenchmarking(false);
    }
  }, [redetectPerformance]);

  return (
    <div className="settings-container">
      <div className="settings-header">
        <h1 className="settings-title">{t("common.settings")}</h1>
        <p className="settings-subtitle">{t("settings.subtitle")}</p>
      </div>

      <div className="settings-section">
        <div className="performance-section-header">
          <h3 className="settings-section-title">
            {t("settings.performance")}
          </h3>
          <IconButton
            className={`performance-section-reload-button ${
              isBenchmarking ? "spinning" : ""
            }`}
            onClick={isBenchmarking ? undefined : handleReBenchmark}
            size="s"
            mode="outline"
            disabled={isBenchmarking}
          >
            <RefreshIcon />
          </IconButton>
        </div>
        <div className="settings-content">
          <p className="settings-section-description">
            {t("settings.performanceDescription")}
          </p>

          <div
            className={`performance-options ${
              isBenchmarking ? "disabled" : ""
            }`}
          >
            <Chip
              mode="outline"
              onClick={() => !isBenchmarking && handlePerformanceChange("low")}
              className={`performance-chip ${
                performanceLevel === "low" ? "performance-chip--selected" : ""
              }`}
            >
              {t("settings.performanceLow")}
            </Chip>
            <Chip
              mode="outline"
              onClick={() =>
                !isBenchmarking && handlePerformanceChange("medium")
              }
              className={`performance-chip ${
                performanceLevel === "medium"
                  ? "performance-chip--selected"
                  : ""
              }`}
            >
              {t("settings.performanceMedium")}
            </Chip>
            <Chip
              mode="outline"
              onClick={() => !isBenchmarking && handlePerformanceChange("high")}
              className={`performance-chip ${
                performanceLevel === "high" ? "performance-chip--selected" : ""
              }`}
            >
              {t("settings.performanceHigh")}
            </Chip>
          </div>

          <div className="performance-description">
            <p className="performance-help-text">
              {performanceLevel === "low" &&
                t("settings.performanceLowDescription")}
              {performanceLevel === "medium" &&
                t("settings.performanceMediumDescription")}
              {performanceLevel === "high" &&
                t("settings.performanceHighDescription")}
            </p>
            {isAutoDetected && !isBenchmarking && (
              <p className="auto-detected-note">
                ✓ {t("settings.autoDetected")}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="settings-section">
        <h3 className="settings-section-title">{t("settings.audioHaptics")}</h3>
        <div className="settings-content">
          <p className="settings-section-description">
            {t("settings.audioHapticsDescription")}
          </p>

          <div className="language-options">
            <div
              className="language-item"
              onClick={() => {
                const next = !musicEnabled;
                setMusicEnabled(next);
                applyMusicSetting(next);
              }}
            >
              <div className="language-option">
                <span className="language-native">{t("settings.music")}</span>
                <span className="language-english">
                  {musicEnabled ? t("common.on") : t("common.off")}
                </span>
              </div>
            </div>

            <div
              className="language-item"
              onClick={() => {
                const next = !soundsEnabled;
                setSoundsEnabled(next);
                applySoundsSetting(next);
              }}
            >
              <div className="language-option">
                <span className="language-native">{t("settings.sounds")}</span>
                <span className="language-english">
                  {soundsEnabled ? t("common.on") : t("common.off")}
                </span>
              </div>
            </div>

            <div
              className="language-item"
              onClick={() => {
                const next = !hapticsEnabled;
                setHapticsEnabled(next);
                applyHapticsSetting(next);
              }}
            >
              <div className="language-option">
                <span className="language-native">{t("settings.haptics")}</span>
                <span className="language-english">
                  {hapticsEnabled ? t("common.on") : t("common.off")}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="settings-section">
        <h3 className="settings-section-title">{t("settings.language")}</h3>
        <div className="settings-content">
          <div className="language-options">
            {availableLanguages.map((language) => (
              <div
                key={language.code}
                className={`language-item ${
                  currentLanguage === language.code
                    ? "language-item--selected"
                    : ""
                }`}
                onClick={() => handleLanguageChange(language.code)}
              >
                <div className="language-option">
                  <span className="language-native">
                    {language.name.native}
                  </span>
                  <span className="language-english">
                    {language.name.english}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
